import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { ErrorMessage } from '@zocdoc-frontend-common/components-errormessage';
import { clearfix } from '@zocdoc-frontend-common/styles-grid';
import { outline } from '@zocdoc-frontend-common/styles-mixins';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import { navy } from '@zocdoc/branding/colors';
import CheckboxActive from './CheckboxActive.svg';
import CheckboxEnabled from './CheckboxEnabled.svg';
import CheckboxError from './CheckboxError.svg';

export default class Checkbox extends PureComponent {
    static propTypes = {
        checked: PropTypes.bool,
        invalidMessage: PropTypes.string,
        onChecked: PropTypes.func,
        outerProps: PropTypes.object,
        wrapperProps: PropTypes.object,
        dataValue: PropTypes.string,
        inputProps: PropTypes.object,
    };

    static defaultProps = {
        checked: false,
        onChecked: noop,
        outerProps: {},
        wrapperProps: {},
        inputProps: {},
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        /**
         * This allows the Checkbox component to operate in both
         * controlled and uncontrolled mode.
         */
        if (nextProps.checked !== prevState.cachedCheckedProp) {
            return {
                cachedCheckedProp: nextProps.checked,
                checked: !!nextProps.checked,
            };
        }

        return null;
    }

    state = {};

    render() {
        const {
            children,
            dataValue,
            outerProps,
            wrapperProps,
            inputProps,
            ...props
        } = this.props;

        return (
            <div data-test="checkbox" {...filterToValidAttributes(outerProps)}>
                <Wrapper data-test="checkbox-inner" {...wrapperProps}>
                    <Label
                        data-test="checkbox-label"
                        {...filterToValidAttributes(props)}
                    >
                        <Input
                            checked={this.state.checked}
                            data-test="checkbox-input"
                            data-value={dataValue}
                            onChange={this.handleChange}
                            onInput={this.handleChange}
                            tabIndex="0"
                            type="checkbox"
                            {...filterToValidAttributes(inputProps)}
                        />
                        {this.renderIcon()}
                        <span data-test="checkbox-label-text">{children}</span>
                    </Label>
                </Wrapper>
                {this.renderInvalidErrorMessage()}
            </div>
        );
    }

    renderInvalidErrorMessage() {
        if (this.props.invalidMessage) {
            return (
                <div data-test="checkbox-error">
                    <ErrorMessage>{this.props.invalidMessage}</ErrorMessage>
                </div>
            );
        }
    }

    renderIcon() {
        const { checked } = this.state;
        const { invalidMessage, iconStyle } = this.props;

        let src;
        if (checked) {
            src = CheckboxActive;
        } else if (invalidMessage) {
            src = CheckboxError;
        } else {
            src = CheckboxEnabled;
        }

        return (
            <CheckboxIcon
                data-icon
                data-test="checkbox-icon"
                src={src}
                style={iconStyle}
            />
        );
    }

    handleChange = (e) => {
        this.setState({ checked: e.target.checked });
        this.props.onChecked(e.target.checked, e);
    };
}

export const Wrapper = styled.div`
    ${clearfix}
`;

export const Label = styled.label`
    color: ${navy};
    cursor: pointer;
    display: inline-flex;
    flex-direction: row;
    font: 15px/24px ${FontFamily.SharpSansMedium};
    padding-top: 5px;
    padding-left: 4px;
    position: relative;
    width: 100%;
`;

export const Input = styled.input`
    cursor: pointer;
    height: 16px;
    left: 8px;
    margin: 0;
    opacity: 0;
    overflow: hidden;
    position: absolute;
    top: 7px;
    width: 16px;

    &:focus + [data-icon] {
        ${outline}
    }
`;

export const CheckboxIcon = styled.img`
    flex: 0 0 16px;
    height: 16px;
    margin: 3px 12px 8px 4px;
    pointer-events: none;
    width: 16px;
`;
